import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  allRulesSatisfied,
  evaluatePasswordRules,
  type PasswordRules,
} from '@wineryxplorer/shared';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { PasswordRequirements } from '../components/auth/PasswordRequirements';
import { LoginAccountPrompt } from '../components/auth/LoginAccountPrompt';
import { LoginOAuthPanel } from '../components/auth/LoginOAuthPanel';
import { LoginTabs } from '../components/auth/LoginTabs';
import { SignupIdentityFields } from '../components/auth/SignupIdentityFields';
import { SignupAgeFields } from '../components/auth/SignupAgeFields';
import {
  birthdayToDate,
  getBirthdayError,
  getEmailError,
  getNameError,
  getPasswordError,
  getUserNameError,
} from '../components/auth/loginValidation';
import { trpc } from '../lib/trpc';
import { useAuth } from '../hooks/useAuth';
import { showToast } from '../utils/toast';
import type { LoginTab } from '../components/auth/LoginTabs';

type TabType = LoginTab;

export const Login = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { refetchUser } = useAuth();
  // `?signup=...` opens the signup tab directly, so callers that send someone
  // here to make an account (the /itineraries CTA, the venue-admin prompt on
  // Experiences) land on the right form instead of the sign-in one.
  const [activeTab, setActiveTab] = useState<TabType>(() =>
    searchParams.get('signup') !== null ? 'signup' : 'signin'
  );

  // Email/Password state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [userName, setUserName] = useState('');
  // Date of birth and the country it is judged against. Required to create an
  // account — the age check has to happen before the account exists, not in
  // onboarding afterwards. Defaulting the country would quietly pick a legal
  // age on someone's behalf, so it starts empty.
  const [birthday, setBirthday] = useState('');
  const [country, setCountry] = useState('');
  const [birthdayError, setBirthdayError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [nameError, setNameError] = useState('');
  const [userNameError, setUserNameError] = useState('');
  const [loginError, setLoginError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [passwordRules, setPasswordRules] = useState<PasswordRules>(() =>
    evaluatePasswordRules('')
  );

  // Show friendly error if redirected from a failed auth attempt
  useEffect(() => {
    const error = searchParams.get('error');
    if (error === 'auth_failed') {
      setLoginError('Something went wrong during sign in. Please try again.');
      // Clean up the URL
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Real-time validation helpers — rules live in ../components/auth/loginValidation
  const validateEmail = (emailValue: string) => setEmailError(getEmailError(emailValue));
  const validateName = (nameValue: string) => setNameError(getNameError(nameValue));
  const validateUserName = (userNameValue: string) =>
    setUserNameError(getUserNameError(userNameValue));
  const validateBirthday = (birthdayValue: string, countryValue: string) =>
    setBirthdayError(getBirthdayError(birthdayValue, countryValue));

  // tRPC mutations
  const registerMutation = trpc.auth.register.useMutation();
  const loginMutation = trpc.auth.login.useMutation();

  const isLoading =
    registerMutation.isPending ||
    loginMutation.isPending;

  // Password validation function
  const validatePassword = (pwd: string) => {
    const rules = evaluatePasswordRules(pwd);
    setPasswordRules(rules);
    setPasswordError(getPasswordError(pwd, rules));
  };

  // Email/Password handlers
  const handleEmailSignup = async () => {
    // Basic client-side checks
    if (!email || !name || !userName || !password || !birthday || !country) {
      setLoginError('Please fill in all fields');
      return;
    }

    // The server refuses an underage registration outright; checking here too
    // means someone finds out before their password is sent anywhere.
    const ageError = getBirthdayError(birthday, country);
    if (ageError) {
      setBirthdayError(ageError);
      setLoginError(ageError);
      // Failing the gate is a stop, so the rejected date does not stay sitting
      // in the field waiting to be nudged by a year.
      setBirthday('');
      return;
    }

    setLoginError('');
    setSuccessMessage('');

    try {
      const result = await registerMutation.mutateAsync({
        email,
        password,
        name,
        userName,
        birthday: birthdayToDate(birthday).toISOString(),
        country,
      });
      
      // Store session token and refetch user
      localStorage.setItem('session-token', result.sessionToken);
      await refetchUser();
      
      // User is now logged in, navigate to onboarding
      navigate('/onboarding');
    } catch (error: unknown) {
      const errorMessage = 
        error && typeof error === 'object' && 'message' in error
          ? String(error.message)
          : 'Failed to create account';
      setLoginError(errorMessage);
    }
  };

  const handleEmailSignin = async () => {
    // Basic client-side checks
    if (!email || !password) {
      setLoginError('Please fill in all fields');
      return;
    }

    setLoginError('');
    setSuccessMessage('');

    try {
      const result = await loginMutation.mutateAsync({
        email,
        password,
      });
      
      // Store session token and refetch user
      localStorage.setItem('session-token', result.sessionToken);
      await refetchUser();
      
      if (!result.user.onboardingComplete) {
        navigate('/onboarding');
      } else {
        navigate('/');
      }
    } catch (error: unknown) {
      const errorMessage = 
        error && typeof error === 'object' && 'message' in error
          ? String(error.message)
          : 'Failed to sign in';
      setLoginError(errorMessage);
    }
  };

  // OAuth: Auth.js requires a POST with CSRF token to initiate provider sign-in
  const handleOAuthSignin = async (provider: string) => {
    try {
      const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      // Fetch CSRF token from Auth.js
      const csrfRes = await fetch(`${apiBase}/api/auth/csrf`, { credentials: 'include' });
      const { csrfToken } = await csrfRes.json();

      // Submit a hidden form to POST to the signin endpoint (triggers redirect to provider)
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = `${apiBase}/api/auth/signin/${provider}`;

      const csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = 'csrfToken';
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);

      // Tell Auth.js where to redirect after sign-in
      const callbackInput = document.createElement('input');
      callbackInput.type = 'hidden';
      callbackInput.name = 'callbackUrl';
      callbackInput.value = window.location.origin + '/oauth/callback';
      form.appendChild(callbackInput);

      document.body.appendChild(form);
      form.submit();
    } catch (_error) {
      showToast('Failed to start sign in. Please try again.', 'error');
    }
  };

  const handleGoogleSignin = () => {
    handleOAuthSignin('google');
  };

  const _handleAppleSignin = () => {
    handleOAuthSignin('apple');
  };

  // Switching tabs clears every field-level and form-level message.
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setEmailError('');
    setPasswordError('');
    setNameError('');
    setUserNameError('');
    setBirthdayError('');
    setLoginError('');
    setSuccessMessage('');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center px-4 pt-6 pb-12">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome to WineryXplorer
          </h1>
          <p className="text-gray-600">
            {activeTab === 'signin'
              ? 'Sign in to your account'
              : 'Create your account to get started'}
          </p>
        </div>

        <Card variant="elevated" padding="lg">
          {/* Tabs */}
          <LoginTabs activeTab={activeTab} onSelectTab={handleTabChange} />

          <LoginOAuthPanel onGoogleSignin={handleGoogleSignin} disabled={isLoading} />
          {/* Email/Password Form */}
          <div className="space-y-4">
              {/* Error Message */}
              {loginError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{loginError}</p>
                </div>
              )}

              {/* Success Message */}
              {successMessage && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-sm text-green-600">{successMessage}</p>
                </div>
              )}

              {activeTab === 'signup' && (
                <SignupIdentityFields
                  name={name}
                  nameError={nameError}
                  onNameChange={(newName) => {
                    setName(newName);
                    validateName(newName);
                  }}
                  userName={userName}
                  userNameError={userNameError}
                  onUserNameChange={(newUserName) => {
                    setUserName(newUserName);
                    validateUserName(newUserName);
                  }}
                  disabled={isLoading}
                />
              )}

              {activeTab === 'signup' && (
                <SignupAgeFields
                  birthday={birthday}
                  birthdayError={birthdayError}
                  onBirthdayChange={(newBirthday) => {
                    setBirthday(newBirthday);
                    validateBirthday(newBirthday, country);
                  }}
                  country={country}
                  onCountryChange={(newCountry) => {
                    setCountry(newCountry);
                    validateBirthday(birthday, newCountry);
                  }}
                  disabled={isLoading}
                />
              )}

              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(e) => {
                  const newEmail = e.target.value;
                  setEmail(newEmail);
                  setLoginError('');
                  setSuccessMessage('');
                  if (activeTab === 'signup') {
                    validateEmail(newEmail);
                  } else {
                    setEmailError('');
                  }
                }}
                placeholder="you@example.com"
                error={emailError}
                success={activeTab === 'signup' && email.length > 0 && !emailError}
                fullWidth
                required
                disabled={isLoading}
              />

              <Input
                label="Password"
                type="password"
                value={password}
                onChange={(e) => {
                  const newPassword = e.target.value;
                  setPassword(newPassword);
                  setLoginError('');
                  setSuccessMessage('');
                  if (activeTab === 'signup') {
                    validatePassword(newPassword);
                  } else {
                    setPasswordError('');
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (activeTab === 'signin') {
                      handleEmailSignin();
                    } else {
                      handleEmailSignup();
                    }
                  }
                }}
                placeholder={activeTab === 'signup' ? 'Create a strong password' : 'Your password'}
                error={passwordError}
                success={activeTab === 'signup' && allRulesSatisfied(passwordRules)}
                fullWidth
                required
                disabled={isLoading}
              />

              {activeTab === 'signup' && (
                <div className="mt-2">
                  <PasswordRequirements password={password} rules={passwordRules} />
                </div>
              )}

              {activeTab === 'signin' && (
                <div className="text-right">
                  <button
                    onClick={() => navigate('/forgot-password')}
                    className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={activeTab === 'signin' ? handleEmailSignin : handleEmailSignup}
                loading={isLoading}
                disabled={
                  isLoading ||
                  (activeTab === 'signup' && (!name.trim() || !userName || !email || !password || !birthday || !country || !!nameError || !!userNameError || !!emailError || !!passwordError || !!birthdayError || !allRulesSatisfied(passwordRules))) ||
                  (activeTab === 'signin' && (!email || !password))
                }
              >
                {activeTab === 'signin' ? 'Sign In' : 'Create Account'}
              </Button>
            </div>
          </Card>

        {/* Footer */}
        <LoginAccountPrompt activeTab={activeTab} onSelectTab={setActiveTab} />
      </div>
    </div>
  );
};
